import React, { FC } from 'react';

import { CloudAccountInfoGetResponse } from '@ariksa/jit/api';
import { Box, HStack, Input, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { QueryError } from 'services/types';

import {
  CancelButton,
  defaultFormStyles,
  FormLabel,
  formLabelStyles,
  PrimaryButton,
} from 'components/DataEntry';
import {
  DocumentsIcon,
  ExclamationTriangleIcon,
  ThumbsUpIcon,
} from 'components/Icons';
import { Modal } from 'components/Overlay';
import { selectJit } from 'containers/Jit/selectors';
import { JITConfigurationModalHeader } from 'containers/Jit/Setup/Components/Utils/JITConfigurationModalHeader';
import { JITInformationAlert } from 'containers/Jit/Setup/Components/Utils/JITInformationAlert';
import {
  getJITLoadErrorMessage,
  getJITSaveErrorMessage,
} from 'containers/Jit/Setup/Components/Utils/utils';
import { actions as jitActions } from 'containers/Jit/slice';

interface Props {
  // Shared by account and data-group JIT configuration.
  isOpen: boolean;
  onClose: () => void;
  account: CloudAccountInfoGetResponse;
  dataGroupId?: string;
  dataGroupName?: string;
}

export const ConfigureJitApproval: FC<Props> = props => {
  const { isOpen, onClose, account, dataGroupId, dataGroupName } = props;
  const [duration, setDuration] = React.useState('2');
  const [loadError, setLoadError] = React.useState<QueryError>();
  const [saveError, setSaveError] = React.useState<string>();
  const { autoApprovalConfiguration, jitAction } = useSelector(selectJit);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!isOpen || !account.uuid) return;

    setLoadError(undefined);
    dispatch(
      jitActions.getAutoApprovalConfiguration({
        q: {
          accountId: account.uuid,
          dataGroupId,
        },
        onError: error => setLoadError(error),
      }),
    );
  }, [account.uuid, dataGroupId, dispatch, isOpen]);

  // Until the saved value arrives the field shows a default, and saving it
  // would replace a setting nobody has seen.
  const isLoaded = autoApprovalConfiguration.isSuccess;

  React.useEffect(() => {
    const seconds =
      autoApprovalConfiguration.data?.configuration?.max_duration_seconds ??
      autoApprovalConfiguration.data?.effective_configuration
        ?.max_duration_seconds;

    if (seconds != null) {
      setDuration(String(Math.round(seconds / (60 * 60))));
    }
  }, [autoApprovalConfiguration.data]);

  const errorMessage = loadError
    ? getJITLoadErrorMessage(loadError, dataGroupId)
    : saveError;

  const onClickOk = () => {
    setSaveError(undefined);
    dispatch(
      jitActions.saveAutoApprovalConfiguration({
        q: {
          accountId: account.uuid,
          autoApprovalConfigRequest: {
            max_duration_seconds: Number(duration || 0) * 60 * 60,
          },
          dataGroupId,
        },
        onSuccess: res => {
          onClose();
        },
        onError: error =>
          setSaveError(getJITSaveErrorMessage(error, dataGroupId)),
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      styles={{ body: { px: 6 } }}
      header={
        <JITConfigurationModalHeader
          account={account}
          dataGroupName={dataGroupName}
          label="Configure Auto Approval"
          icon={<ThumbsUpIcon />}
        />
      }
      body={
        <Stack>
          {errorMessage ? (
            <JITInformationAlert
              icon={<ExclamationTriangleIcon color="red" />}
              description={errorMessage}
            />
          ) : (
            <JITInformationAlert
              icon={<DocumentsIcon color="primary" />}
              description="You can customize durations and auto-approvals for every cloud type"
            />
          )}
          <Box px={12} pt={4} hidden={!!loadError}>
            <Stack spacing={1}>
              <FormLabel
                htmlFor="duration"
                label="Maximum Duration"
                styles={formLabelStyles}
              />
              <Box w="70%">
                <HStack>
                  <Input
                    {...defaultFormStyles.textField?.input}
                    value={duration}
                    isDisabled={!isLoaded}
                    onChange={event =>
                      setDuration(event.target.value.replace(/\D/g, ''))
                    }
                  />
                  <Box flexShrink={0}>Hours</Box>
                </HStack>
              </Box>
            </Stack>
          </Box>
        </Stack>
      }
      footer={
        <HStack justify="space-between" w="full">
          <CancelButton onClick={onClose}>CANCEL</CancelButton>
          <PrimaryButton
            px={4}
            onClick={onClickOk}
            isLoading={jitAction.isLoading}
            isDisabled={!isLoaded || !Number(duration)}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
    />
  );
};
